import React, { useMemo, useState } from 'react';
import { ShieldCheck, PencilRuler } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { PermissionsForm } from './PermissionsForm';
import {
  eventTeamApi,
  ALL_PERMISSIONS_FALSE,
  ALL_PERMISSIONS_TRUE,
} from '../../api/eventTeam';
import type { EventManagerPermissions } from '../../types';
import styles from './AddManagerModal.module.css';

interface AddManagerModalProps {
  eventId: string;
  isOpen: boolean;
  onClose: () => void;
  onAdded: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "Editor" preset: hands-on photo team — content actions, no event lifecycle
// or access management. Tuned to be the most useful "non-co-host" default.
const EDITOR_PRESET: EventManagerPermissions = {
  allowEditingEventDetails: false,
  allowEndingEvent: false,
  allowDeletingEvent: false,
  allowCreatingNewAlbum: true,
  allowDeletingOfPhotos: true,
  allowAddingNewPresets: true,
  allowAddingNewFtpAccount: false,
  allowManagingTeam: false,
};

type PresetId = 'full' | 'editor';

function permsEqual(a: EventManagerPermissions, b: EventManagerPermissions): boolean {
  return (Object.keys(b) as (keyof EventManagerPermissions)[]).every((k) => a[k] === b[k]);
}

function detectPreset(perms: EventManagerPermissions): PresetId | null {
  if (permsEqual(perms, ALL_PERMISSIONS_TRUE)) return 'full';
  if (permsEqual(perms, EDITOR_PRESET)) return 'editor';
  return null;
}

export function AddManagerModal({ eventId, isOpen, onClose, onAdded }: AddManagerModalProps) {
  const [email, setEmail] = useState('');
  const [permissions, setPermissions] = useState<EventManagerPermissions>({ ...ALL_PERMISSIONS_FALSE });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const grantedCount = useMemo(
    () => Object.values(permissions).filter(Boolean).length,
    [permissions],
  );
  const activePreset = useMemo(() => detectPreset(permissions), [permissions]);

  const reset = () => {
    setEmail('');
    setPermissions({ ...ALL_PERMISSIONS_FALSE });
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handlePreset = (preset: PresetId) => {
    setPermissions(preset === 'full' ? { ...ALL_PERMISSIONS_TRUE } : { ...EDITOR_PRESET });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_RE.test(normalized)) {
      setError('Enter a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      await eventTeamApi.add(eventId, { email: normalized, permissions });
      toast.success('Manager invited');
      onAdded();
      reset();
      onClose();
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { error?: string } } };
      const msg = e.response?.data?.error;
      if (e.response?.status === 409) {
        setError(msg ?? 'This email is already on the team');
      } else if (e.response?.status === 400) {
        setError(msg ?? 'Invalid request');
      } else {
        setError(msg ?? 'Failed to add manager');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add a manager" size="lg">
      <form className={styles['form']} onSubmit={handleSubmit}>
        <div className={styles['scroll-body']}>
        <div className={styles['email-section']}>
          <Input
            label="Manager's email"
            type="email"
            autoFocus
            autoComplete="off"
            spellCheck={false}
            placeholder="manager@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            error={error ?? undefined}
          />
          <p className={styles['email-help']}>
            We&apos;ll email an invitation. New users gain access automatically the first time they sign in with this Google account.
          </p>
        </div>

        <div className={styles['presets']}>
          <span className={styles['presets-label']}>Quick start</span>
          <div className={styles['presets-chips']}>
            <button
              type="button"
              onClick={() => handlePreset('full')}
              disabled={submitting}
              className={[
                styles['preset-chip'],
                activePreset === 'full' ? styles['preset-active'] : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-pressed={activePreset === 'full'}
            >
              <ShieldCheck className={styles['preset-icon']} />
              Full access
            </button>
            <button
              type="button"
              onClick={() => handlePreset('editor')}
              disabled={submitting}
              className={[
                styles['preset-chip'],
                activePreset === 'editor' ? styles['preset-active'] : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-pressed={activePreset === 'editor'}
            >
              <PencilRuler className={styles['preset-icon']} />
              Editor
            </button>
          </div>
        </div>

        <PermissionsForm value={permissions} onChange={setPermissions} disabled={submitting} />
        </div>

        <div className={styles['actions']}>
          <span className={styles['count']}>
            <strong>{grantedCount}</strong> of 8 permissions granted
          </span>
          <div className={styles['actions-buttons']}>
            <Button type="button" variant="ghost" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={submitting}>
              Send invite
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
